import React from 'react';
import {
  MountingMode,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@grnhse/seedling/lib/birch/components/tabs/';
import type { MyGreenhouseCandidate } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';
import candidateDisplayName from 'talent_matching/components/shared/candidate_display_name';
import { BaseSidePanelRenderProps } from './base_side_panel';
import WorkPreferencesTabPanel from './work_preferences_tab_panel';
import ResumeTabPanel from './resume_tab_panel';
import MatchReportTabPanel from './match_report_tab_panel';
import MatchStrengthExplanation from './match_strength_explanation';
import FeedbackBanner from './feedback_banner';

type Props = Pick<
  BaseSidePanelRenderProps,
  | 'extendedResumePreview'
  | 'modalDocument'
  | 'setExtendedResumePreview'
  | 'setLinkedinPreview'
  | 'setModalDocument'
  | 'allowExtendedPreview'
> & {
  candidate: MyGreenhouseCandidate | undefined;
  hiringPlanId: number;
  userId: number;
  tabsConfig: { id: string; label: string }[];
  selectedTabIndex: number;
  onTabChange: (index: number) => void;
};

const MyGreenhouseSidePanelBody = ({
  candidate,
  hiringPlanId,
  userId,
  tabsConfig,
  selectedTabIndex,
  onTabChange,
  extendedResumePreview,
  modalDocument,
  setExtendedResumePreview,
  setLinkedinPreview,
  setModalDocument,
  allowExtendedPreview,
}: Props) => {
  const renderTabContent = (id: string) => {
    switch (id) {
      case 'work_preferences':
        return <WorkPreferencesTabPanel candidate={candidate ?? null} />;
      case 'resume':
        return (
          <ResumeTabPanel
            candidate={candidate ?? null}
            activeDocument={extendedResumePreview ?? modalDocument ?? null}
            onOpenDocument={
              allowExtendedPreview
                ? (doc) => {
                    setModalDocument(null);
                    setLinkedinPreview(null);
                    setExtendedResumePreview(doc);
                  }
                : (doc) => {
                    setExtendedResumePreview(null);
                    setLinkedinPreview(null);
                    setModalDocument(doc);
                  }
            }
            canDownloadCandidateData={false}
          />
        );
      case 'match_report':
        return (
          <MatchReportTabPanel matchReport={candidate?.matchReport ?? null} />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {candidate && (
        <FeedbackBanner
          key={candidate.id}
          candidateName={candidateDisplayName(candidate.fullName)}
          candidateId={candidate.id}
          hiringPlanId={hiringPlanId}
          userId={userId}
          matchStrength={candidate.matchStrength ?? null}
        />
      )}
      {candidate && (
        <MatchStrengthExplanation
          application={{
            id: candidate.id,
            hiringPlanId,
            matchStrength: candidate.matchStrength,
            matchStrengthExplanation:
              candidate.matchStrengthExplanation ?? undefined,
            matchedSkills: candidate.matchedSkills ?? [],
            numCalibrationSkills: candidate.numCalibrationSkills ?? 0,
          }}
          showExplanationKey="my_greenhouse/match_strength_explanation"
          hideIndustries
          calibrationFree
        />
      )}
      <Tabs index={selectedTabIndex} onChange={onTabChange}>
        <TabList>
          {tabsConfig.map((tab, index) => (
            <Tab key={tab.id} index={index}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels mountingMode={MountingMode.Selected}>
          {tabsConfig.map((tab, index) => (
            <TabPanel key={tab.id} index={index}>
              {renderTabContent(tab.id)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

export default MyGreenhouseSidePanelBody;
